import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, User, Phone, MapPin,
  ChevronDown, ChevronUp,
  Clock, CheckCircle, Truck, AlertCircle, XCircle, Edit3, Save, X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './UserPanel.css';

const API_ROOT = process.env.REACT_APP_API_URL || (window.location.port === '3000'
  ? '/api'
  : `${process.env.PUBLIC_URL || ''}/api`);

const STATUS_CONFIG = {
  pending:    { label: 'Pending',    icon: Clock,         color: 'amber'  },
  processing: { label: 'Processing', icon: AlertCircle,   color: 'blue'   },
  shipped:    { label: 'Shipped',    icon: Truck,         color: 'teal'   },
  delivered:  { label: 'Delivered',  icon: CheckCircle,   color: 'green'  },
  cancelled:  { label: 'Cancelled',  icon: XCircle,       color: 'red'    },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`up-status-badge status-${cfg.color}`}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
}

function OrderRow({ order, onDelete }) {
  const [open, setOpen] = useState(false);
  const itemCount = order.items?.length || 0;

  return (
    <div className="up-order-card">
      <div className="up-order-header" onClick={() => setOpen(v => !v)}>
        <div className="up-order-id">
          <Package size={15} />
          <span>Order #{order.id}</span>
        </div>
        <div className="up-order-meta">
          <span className="up-order-date">{new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
          <span className="up-order-items">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
          <span className="up-order-total">${parseFloat(order.total).toFixed(2)}</span>
          <StatusBadge status={order.status} />
          <button className="up-order-toggle" aria-label={open ? 'Collapse' : 'Expand'}>
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="up-order-items-list">
          <div className="up-items-table">
            <div className="up-items-thead">
              <span>Product</span><span>Price</span><span>Qty</span><span>Subtotal</span>
            </div>
            {(order.items || []).map((item, i) => {
              const price = parseFloat(item.price);
              const subtotal = price * item.qty;
              return (
                <div className="up-items-row" key={i}>
                  <span>{item.product_name}</span>
                  <span>${price.toFixed(2)}</span>
                  <span>×{item.qty}</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
              );
            })}
            <div className="up-items-total">
              <span>Total</span>
              <span></span><span></span>
              <span><strong>${parseFloat(order.total).toFixed(2)}</strong></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function UserPanel() {
  const { user, token, isLoggedIn, updateProfile } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [saving, setSaving]   = useState(false);
  const [profile, setProfile] = useState({ name: '', phone: '', address: '' });

  // Redirect if not logged in
  useEffect(() => {
    if (!isLoggedIn) navigate('/');
  }, [isLoggedIn, navigate]);

  // Sync profile fields when user loads
  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || '', phone: user.phone || '', address: user.address || '' });
    }
  }, [user]);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res  = await fetch(`${API_ROOT}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setOrders(data.data || []);
    } catch {
      /* network error */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaveErr('');
    setSaving(true);
    try {
      await updateProfile(profile);
      setEditing(false);
    } catch (err) {
      setSaveErr(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      const res = await fetch(`${API_ROOT}/orders/${orderId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setOrders(prev => prev.filter(o => o.id !== orderId));
    } catch (err) {
      alert(err.message || 'Failed to delete order.');
    }
  };

  if (!isLoggedIn) return null;

  return (
    <div className="up-root section">
      <div className="container">
        <div className="up-page-header">
          <p className="eyebrow">My account</p>
          <h1 className="up-title">Your dashboard</h1>
        </div>

        <div className="up-layout">
          {/* ── Profile card ── */}
          <aside className="up-profile-card surface-card">
            <div className="up-profile-avatar">
              {user?.name ? user.name[0].toUpperCase() : user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <h2 className="up-profile-name">{user?.name || 'Complete your profile'}</h2>
            <p className="up-profile-email">{user?.email}</p>

            {!editing ? (
              <div className="up-profile-info">
                <div className="up-info-row"><Phone size={14} /><span>{user?.phone || '—'}</span></div>
                <div className="up-info-row"><MapPin size={14} /><span>{user?.address || '—'}</span></div>
                <button className="up-edit-btn" onClick={() => setEditing(true)}>
                  <Edit3 size={13} /> Edit profile
                </button>
              </div>
            ) : (
              <form className="up-edit-form" onSubmit={handleSaveProfile}>
                <div className="up-edit-field">
                  <label><User size={13} /> Full name</label>
                  <input type="text" value={profile.name} onChange={e => setProfile(p => ({...p, name: e.target.value}))} required />
                </div>
                <div className="up-edit-field">
                  <label><Phone size={13} /> Phone</label>
                  <input type="tel" value={profile.phone} onChange={e => setProfile(p => ({...p, phone: e.target.value}))} required />
                </div>
                <div className="up-edit-field">
                  <label><MapPin size={13} /> Address</label>
                  <textarea rows={3} value={profile.address} onChange={e => setProfile(p => ({...p, address: e.target.value}))} required />
                </div>
                {saveErr && <p className="up-err">{saveErr}</p>}
                <div className="up-edit-actions">
                  <button type="submit" className="up-save-btn" disabled={saving}>
                    <Save size={13} /> {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" className="up-cancel-btn" onClick={() => { setEditing(false); setSaveErr(''); }}>
                    <X size={13} /> Cancel
                  </button>
                </div>
              </form>
            )}
          </aside>

          {/* ── Orders section ── */}
          <main className="up-orders-section">
            <div className="up-orders-header">
              <h2 className="up-orders-title">Order history</h2>
              <span className="up-orders-count">{orders.length} order{orders.length !== 1 ? 's' : ''}</span>
            </div>

            {loading ? (
              <div className="up-loading">
                <div className="up-spinner" />
                <p>Loading your orders…</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="up-empty-orders">
                <div className="up-empty-icon"><Package size={40} strokeWidth={1.3} /></div>
                <h3>No orders yet</h3>
                <p>Browse our product catalog and place your first order.</p>
              </div>
            ) : (
              <div className="up-orders-list">
                {orders.map(order => <OrderRow key={order.id} order={order} onDelete={handleDeleteOrder} />)}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
